
import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Printer, Download, Calculator, Plus, Search, FileText, Send, CheckCircle2, ArrowLeft, Mail, Sparkles, Loader, X, Home, Euro, Info, TrendingUp, Check, Bold, Italic, List, AlignLeft, User, MapPin, ChevronDown, Briefcase, ExternalLink, ArrowUpRight, Key, Edit, PenTool, Building, Square, BedDouble, Bath, Users } from 'lucide-react';
import { storage } from '../services/storage';
import { emailService } from '../services/email';
import { Proposal, Deal, Owner, Property, DealStage, Agency, Note, UserProfile } from '../types';
import { aiService } from '../services/aiService';
import { useModal } from './ui/ModalProvider';
import { PropertyInfoModal } from './ui/PropertyInfoModal';

interface ProposalGeneratorProps {
    dealId?: string | null;
    selectedAgencyId?: string | null;
    onBack?: () => void;
    onNavigateToDeal?: (dealId: string) => void;
    onNavigateToManaged?: (propertyId: string) => void;
    onNavigateToContract?: (dealId: string) => void; // NEW
}

const SETUP_TIERS = [
    { label: 'Studio / T1', price: 349, bedrooms: 0 },
    { label: 'T2', price: 399, bedrooms: 1 },
    { label: 'T3', price: 449, bedrooms: 2 },
    { label: 'T4', price: 499, bedrooms: 3 },
    { label: 'Villa / T5+', price: 599, bedrooms: 4 },
];

// --- RICH TEXT EDITOR COMPONENT ---
const RichTextEditor: React.FC<{ value: string; onChange: (val: string) => void }> = ({ value, onChange }) => {
    const editorRef = useRef<HTMLDivElement>(null);

    const execCmd = (command: string) => {
        document.execCommand(command, false, undefined);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    return (
        <div className="border border-slate-300 rounded-lg overflow-hidden bg-white flex flex-col h-80 focus-within:ring-2 focus-within:ring-amber-500 focus-within:border-transparent transition-all">
            <div className="flex items-center space-x-1 p-2 border-b border-slate-200 bg-slate-50">
                <button onClick={() => execCmd('bold')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Gras"><Bold className="w-4 h-4" /></button>
                <button onClick={() => execCmd('italic')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Italique"><Italic className="w-4 h-4" /></button>
                <div className="w-px h-4 bg-slate-300 mx-2"></div>
                <button onClick={() => execCmd('insertUnorderedList')} className="p-1.5 hover:bg-slate-200 rounded text-slate-600" title="Liste à puces"><List className="w-4 h-4" /></button>
            </div>
            <div 
                ref={editorRef}
                className="flex-1 p-4 outline-none overflow-y-auto text-sm text-slate-900 font-sans"
                contentEditable
                suppressContentEditableWarning
                onBlur={(e) => onChange(e.currentTarget.innerHTML)}
                dangerouslySetInnerHTML={{ __html: value }}
                style={{ lineHeight: '1.6' }}
            />
        </div>
    );
};

export const ProposalGenerator: React.FC<ProposalGeneratorProps> = ({ dealId, selectedAgencyId, onBack, onNavigateToDeal, onNavigateToManaged, onNavigateToContract }) => {
  const { toast, confirm, alert, prompt } = useModal();
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'preview'>('list');
  
  // Data State
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [owners, setOwners] = useState<Owner[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  
  // Creation/Edit State
  const [selectedDealId, setSelectedDealId] = useState<string>(dealId || '');
  const [formData, setFormData] = useState<Partial<Proposal>>({
      nightlyRate: 90,
      occupancyRate: 0.70,
      commissionRate: 25,
      cleaningFee: 0, // Hidden from form, assumed paid by guest
      setupFee: 349,
      isSetupFeeOffered: false,
      status: 'DRAFT'
  });

  // State for property info modal
  const [showPropertyInfoModal, setShowPropertyInfoModal] = useState(false);
  const [missingFieldsList, setMissingFieldsList] = useState<string[]>([]);
  const [propertyForModal, setPropertyForModal] = useState<Property | null>(null);

  // Deal Search State
  const [dealSearchQuery, setDealSearchQuery] = useState('');
  const [isDealDropdownOpen, setIsDealDropdownOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  
  // Emailing State
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState({
      from: '',
      to: '',
      cc: '',
      replyTo: '',
      subject: '',
      body: ''
  });
  
  // Email domain config (chargé depuis les paramètres)
  const [emailDomain, setEmailDomain] = useState<string>('');
  
  // Loading & AI
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Load Data
  useEffect(() => {
      const fetchData = async () => {
          const userSession = await storage.getUser();
          
          const promises: any[] = [
              storage.getProposals(),
              storage.getDeals(selectedAgencyId), // Filtered by Agency
              storage.getOwners(selectedAgencyId), // Filtered by Agency
              storage.getProperties(selectedAgencyId), // Filtered by Agency
              storage.getAllAgencies()
          ];

          if (userSession) {
              promises.push(storage.getProfile(userSession.id));
          }

          const results = await Promise.all(promises);
          
          let fetchedProposals = results[0] as Proposal[];
          const fetchedDeals = results[1] as Deal[];
          const fetchedOwners = results[2];
          const fetchedProperties = results[3];
          const fetchedAgencies = results[4];

          // Filter proposals based on available deals (which are filtered by agency)
          // If a proposal is for a deal that is NOT in fetchedDeals, exclude it (unless global view)
          if (selectedAgencyId) {
              const visibleDealIds = new Set(fetchedDeals.map(d => d.id));
              fetchedProposals = fetchedProposals.filter(p => visibleDealIds.has(p.dealId));
          }

          setProposals(fetchedProposals);
          setDeals(fetchedDeals);
          setOwners(fetchedOwners);
          setProperties(fetchedProperties);
          setAgencies(fetchedAgencies);
          if (results[5]) setCurrentUser(results[5]);
          
          // Charger le domaine email depuis les paramètres
          const emailDomainConfig = await storage.getSystemConfig('email_domain');
          if (emailDomainConfig?.domain) {
              setEmailDomain(emailDomainConfig.domain);
          } else {
              // Si pas de config, utiliser la valeur par défaut mais afficher un avertissement
              setEmailDomain('@agences-placid.com');
              console.warn('Domaine email non configuré, utilisation de la valeur par défaut');
          }

          if (dealId) {
              setSelectedDealId(dealId);
              // Check if proposal already exists for this deal
              const existing = fetchedProposals.find((prop: Proposal) => prop.dealId === dealId);
              if (existing) {
                  setFormData(existing);
                  setViewMode('preview');
              } else {
                  setViewMode('create');
                  // Pre-fill setup fee based on property
                  const deal = fetchedDeals.find((x: Deal) => x.id === dealId);
                  const property = fetchedProperties.find((x: Property) => x.id === deal?.propertyId);
                  if (property) autoSetSetupFee(property.bedrooms || 0);
              }
          }
      };
      fetchData();
  }, [dealId, selectedAgencyId]);

  // Click outside listener for search dropdown
  useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
          if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
              setIsDealDropdownOpen(false);
          }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- LOGIC ---

  const generateReference = (agencyCity: string = 'GLO') => {
      const year = new Date().getFullYear();
      const random = Math.floor(1000 + Math.random() * 9000);
      const cityCode = agencyCity.substring(0, 3).toUpperCase();
      return `PROP-${cityCode}-${year}-${random}`;
  };

  const getDealLabel = (d: Deal) => {
      const o = owners.find(x => x.id === d.ownerId);
      const p = properties.find(x => x.id === d.propertyId);
      return `${o?.firstName} ${o?.lastName} - ${p?.address} (${p?.city})`;
  };

  const autoSetSetupFee = (bedrooms: number) => {
      // Find the closest tier
      const tier = SETUP_TIERS.reduce((prev, curr) => {
        return (Math.abs(curr.bedrooms - bedrooms) < Math.abs(prev.bedrooms - bedrooms) ? curr : prev);
      });
      setFormData(prev => ({ ...prev, setupFee: tier.price }));
  };

  const handleAiEstimate = async () => {
      const deal = deals.find(d => d.id === selectedDealId);
      if (!deal) return;
      const prop = properties.find(p => p.id === deal.propertyId);
      if (!prop) return;

      // Vérifier si toutes les informations nécessaires sont disponibles
      const missingFields: string[] = [];
      if (!prop.address) missingFields.push('Adresse');
      if (!prop.surface) missingFields.push('Surface');
      if (!prop.bedrooms) missingFields.push('Chambres');
      if (!prop.bathrooms) missingFields.push('Salles de bain');
      if (!prop.maxGuests) missingFields.push('Capacité max voyageurs');

      // Si des champs manquent, ouvrir la modale de collecte
      if (missingFields.length > 0) {
          setMissingFieldsList(missingFields);
          setPropertyForModal(prop);
          setShowPropertyInfoModal(true);
          return;
      }

        // Si toutes les infos sont présentes, procéder directement à l'estimation
        setIsAiLoading(true);
        try {
            // Construire l'adresse complète avec code postal et ville
            const fullAddress = [
                prop.address,
                prop.zipCode,
                prop.city
            ].filter(Boolean).join(', ');
            
            const data = await aiService.estimateProposal({
                address: fullAddress || prop.address || '',
                propertyType: prop.type,
                city: prop.city,
                surface: prop.surface,
                bedrooms: prop.bedrooms,
                bathrooms: prop.bathrooms,
                maxGuests: prop.maxGuests,
            });
          
          if (data.nightlyRate && data.occupancyRate) {
              setFormData(prev => ({
                  ...prev,
                  nightlyRate: data.nightlyRate,
                  occupancyRate: data.occupancyRate
              }));
              toast.success("Estimation IA générée !");
          }
      } catch (e: any) {
          console.error("AI Error", e);
          toast.error("Erreur estimation AI: " + (e.message || "Erreur inconnue"));
      } finally {
          setIsAiLoading(false);
      }
  };

  const calculateFinancials = () => {
      const nightly = formData.nightlyRate || 0;
      const occupancy = formData.occupancyRate || 0;
      const comm = (formData.commissionRate || 25) / 100;
      
      const monthlyRevenue = Math.round(nightly * 30 * occupancy);
      const commissionAmount = Math.round(monthlyRevenue * comm);
      
      const netMonthly = monthlyRevenue - commissionAmount;
      
      return {
          monthlyGross: monthlyRevenue,
          annualGross: monthlyRevenue * 12,
          netMonthly: netMonthly,
          netAnnual: netMonthly * 12,
          commissionAmount
      };
  };

  const handleSave = async (status: 'DRAFT' | 'SENT' = 'DRAFT') => {
      if (!selectedDealId) {
          toast.error('Veuillez sélectionner un deal');
          return;
      }
      
      let dealToUpdate = deals.find(d => d.id === selectedDealId);
      if (!dealToUpdate) {
          toast.error('Deal introuvable');
          return;
      }
      
      const agency = agencies.find(a => a.id === dealToUpdate.agencyId);
      
      // Vérifier qu'une agence est associée au deal
      if (!agency) {
          toast.error('Aucune agence associée à ce deal. Veuillez sélectionner une agence pour ce deal avant de créer une proposition.');
          return;
      }
      
      // Vérifier que les infos de l'agence sont complètes
      if (!agency.city || !agency.email || !agency.legalStructure || !agency.siret) {
          toast.error('Les informations de l\'agence sont incomplètes. Veuillez compléter les informations de l\'agence dans les paramètres.');
          return;
      }
      
      const financials = calculateFinancials();
      
      // Generate reference if not exists
      const reference = (formData as Proposal).reference || generateReference(agency.city);

      const newProposal: Proposal = {
          id: (formData as Proposal).id || crypto.randomUUID(),
          dealId: selectedDealId,
          reference: reference,
          createdAt: new Date().toISOString(),
          status: status,
          nightlyRate: formData.nightlyRate || 0,
          occupancyRate: formData.occupancyRate || 0,
          commissionRate: formData.commissionRate || 25,
          cleaningFee: formData.cleaningFee || 0,
          setupFee: formData.setupFee || 0,
          isSetupFeeOffered: formData.isSetupFeeOffered || false,
          estimatedMonthlyRevenue: financials.monthlyGross,
          estimatedAnnualRevenue: financials.annualGross,
          estimatedNetOwner: financials.netAnnual
      };

      await storage.saveProposal(newProposal);
      
      // 1. UPDATE DEAL VALUE (Revenue Agency = Commission) & ADD NOTE
      if (dealToUpdate) {
          const agencyRevenue = financials.commissionAmount;
          
          // Create a note about the update
          const updateNote: Note = {
              id: Date.now().toString(),
              content: `💰 Revenu mensuel estimé mis à jour : ${agencyRevenue.toLocaleString()} € (via proposition ${reference})`,
              createdAt: new Date().toISOString(),
              authorName: 'Système'
          };

          // Create Updated Deal Object with new Value AND new Note AND Link Proposal
          const dealWithNewValue = { 
              ...dealToUpdate, 
              value: agencyRevenue,
              notes: [...(dealToUpdate.notes || []), updateNote],
              proposalId: newProposal.id // Link proposal immediately so "View Prop" button appears
          };
          
          await storage.saveDeal(dealWithNewValue);
          
          // Update reference for next step
          dealToUpdate = dealWithNewValue;
          
          // Update local state to reflect value change immediately
          setDeals(prev => prev.map(d => d.id === dealToUpdate!.id ? dealToUpdate! : d));
      }

      // 2. MARK SENT (Using the deal object that ALREADY has the new value and note)
      if (status === 'SENT' && dealToUpdate) {
          const { deal: finalDeal } = await storage.markProposalSent(newProposal, dealToUpdate, false);
          // Update state again with the final deal (that now has notes + status + correct value)
          setDeals(prev => prev.map(d => d.id === finalDeal.id ? finalDeal : d));
      }

      setFormData(newProposal);
      setViewMode('preview');
      storage.getProposals().then(allProposals => {
          // Re-filter if necessary
          if (selectedAgencyId) {
              const visibleDealIds = new Set(deals.map(d => d.id));
              setProposals(allProposals.filter(p => visibleDealIds.has(p.dealId)));
          } else {
              setProposals(allProposals);
          }
      });
      
      if (status === 'SENT') toast.success("Proposition marquée comme envoyée !");
      else toast.success("Proposition sauvegardée !");
  };

  const handleAcceptProposal = async () => {
      if (await confirm({
          title: "Valider la proposition ?",
          message: "Cela validera la proposition et passera le deal à l'étape 'Proposition Validée'. Vous pourrez ensuite générer le contrat.",
          confirmText: "Oui, valider",
          cancelText: "Annuler"
      })) {
          const deal = deals.find(d => d.id === selectedDealId);
          const property = properties.find(p => p.id === deal?.propertyId);
          
          if (deal && property && (formData as Proposal).id) {
              try {
                  const { proposal: updatedProposal, deal: updatedDeal } = await storage.acceptProposal(formData as Proposal, deal, property);
                  
                  // Update local state IMMEDIATELY
                  setFormData(updatedProposal);
                  setDeals(prev => prev.map(d => d.id === updatedDeal.id ? updatedDeal : d));
                  
                  // CRITICAL: Refresh proposals list to ensure persistence in List View
                  const refreshedProposals = await storage.getProposals();
                  if (selectedAgencyId) {
                      const visibleDealIds = new Set(deals.map(d => d.id));
                      setProposals(refreshedProposals.filter(p => visibleDealIds.has(p.dealId)));
                  } else {
                      setProposals(refreshedProposals);
                  }
                  
                  toast.success("Proposition validée ! Passez maintenant aux Contrats.");
              } catch (e: any) {
                  console.error(e);
                  // SPECIFIC DB CHECK CONSTRAINT ERROR HANDLING
                  if (e.message?.includes('proposals_status_check') || e.code === '23514') {
                      alert({
                          title: "Mise à jour requise",
                          message: "La base de données doit être mise à jour pour accepter le statut 'ACCEPTED'.\n\nVeuillez exécuter le script 'db_fix_proposals.sql' dans l'éditeur SQL de Supabase."
                      });
                  } else {
                      toast.error("Erreur : " + e.message);
                  }
              }
          }
      }
  };

  const getPdfOptions = () => {
      const reference = (formData as Proposal).reference || 'Placid';
      return {
          margin: 0,
          filename: `Proposition_conciergerie-${reference}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
  };

  const preparePdfClone = () => {
      const element = document.getElementById('proposal-pdf-content');
      if (!element) return null;

      // Clone the element
      const clone = element.cloneNode(true) as HTMLElement;
      
      // Remove browser-specific centering and shadows to ensure fit
      clone.classList.remove('mx-auto');
      clone.classList.remove('shadow-2xl');
      clone.style.margin = '0';
      clone.style.boxShadow = 'none';
      
      // Remove blur specifically for PDF as it causes artifacts/slowness
      // AND REMOVE THE DECORATIVE ELEMENT COMPLETELY AS REQUESTED
      const blurredElements = clone.querySelectorAll('.blur-3xl');
      blurredElements.forEach(el => el.remove());

      // Ensure dimensions are strict for A4 using PIXELS to avoid rounding errors
      // A4 at 96 DPI is approx 794px width. We set strict width.
      clone.style.width = '794px'; 
      clone.style.minWidth = '794px';
      clone.style.maxWidth = '794px';
      
      // Reduce height slightly to 296.5mm to prevent rounding errors causing a second blank page
      clone.style.height = '1120px'; // Approx 296.5mm
      clone.style.maxHeight = '1120px';
      clone.style.overflow = 'hidden';

      return clone;
  };

  const generatePDF = async () => {
      // Vérifier que l'agence est présente
      const deal = deals.find(d => d.id === selectedDealId);
      const agency = agencies.find(a => a.id === deal?.agencyId);
      
      if (!agency) {
          toast.error('Aucune agence associée à ce deal. Impossible de générer le PDF.');
          return;
      }
      
      if (!agency.city || !agency.email || !agency.legalStructure || !agency.siret || !agency.address) {
          toast.error('Les informations de l\'agence sont incomplètes. Veuillez compléter les informations de l\'agence dans les paramètres.');
          return;
      }
      
      const clone = preparePdfClone();
      if (!clone) return;
      
      setIsDownloadingPdf(true);
      
      // Mount clone off-screen
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const opt = getPdfOptions();

      try {
          // @ts-ignore
          if (window.html2pdf) {
              // @ts-ignore
              await window.html2pdf().set(opt).from(clone).save();
              toast.success("PDF téléchargé !");
          } else {
              toast.error("Erreur: Librairie PDF non chargée.");
          }
      } catch (e) {
          console.error(e);
          toast.error("Erreur lors de la génération PDF.");
      } finally {
          document.body.removeChild(wrapper);
          setIsDownloadingPdf(false);
      }
  };

  const getPdfBase64 = async () => {
      const clone = preparePdfClone();
      if (!clone) return null;

      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '0';
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      const opt = getPdfOptions();

      try {
          // @ts-ignore
          if (window.html2pdf) {
              // @ts-ignore
              const pdfDataUri = await window.html2pdf().set(opt).from(clone).output('datauristring');
              return pdfDataUri.split(',')[1]; // Remove prefix
          }
          return null;
      } catch (e) {
          console.error(e);
          return null;
      } finally {
          document.body.removeChild(wrapper);
      }
  };

  const handlePrepareEmail = () => {
      const deal = deals.find(d => d.id === selectedDealId);
      const owner = owners.find(o => o.id === deal?.ownerId);
      const agency = agencies.find(a => a.id === deal?.agencyId);
      const prop = properties.find(p => p.id === deal?.propertyId);

      if (!deal || !owner) {
          toast.error('Deal ou propriétaire manquant');
          return;
      }

      // Vérifier qu'une agence est associée au deal
      if (!agency) {
          toast.error('Aucune agence associée à ce deal. Veuillez sélectionner une agence pour ce deal avant de créer une proposition.');
          return;
      }

      // Toutes les infos doivent venir de l'agence, pas de valeurs par défaut
      if (!agency.city || !agency.address || !agency.email || !agency.phone || !agency.legalStructure || !agency.siret) {
          toast.error('Les informations de l\'agence sont incomplètes. Veuillez compléter les informations de l\'agence dans les paramètres.');
          return;
      }

      const city = agency.city;
      const agencyAddress = agency.address;
      const agencyPhone = agency.phone;
      const agencyEmail = agency.email;
      const agencyLegal = agency.legalStructure;
      const agencySiret = agency.siret;
      
      // Valider que l'email de l'agence se termine par le domaine configuré
      if (!emailDomain) {
          toast.error('Le domaine email n\'est pas configuré. Veuillez le configurer dans les paramètres.');
          return;
      }
      
      if (!agencyEmail.endsWith(emailDomain)) {
          toast.error(`L'email de l'agence (${agencyEmail}) doit se terminer par ${emailDomain}. Veuillez corriger l'email de l'agence dans les paramètres.`);
          return;
      }
      
      // Utiliser l'email de l'agence directement au lieu de générer un email basé sur la ville
      const fromEmail = agencyEmail;
      const fromName = `Agence Placid ${city}`;
      const fromString = `${fromName} <${fromEmail}>`;

      const subject = `Proposition de gestion en conciergerie pour votre bien à ${prop?.city || ''}`;
      
      const financialSnapshot = (formData as Proposal).estimatedNetOwner.toLocaleString();

      const userSignature = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : "L'équipe";

      const body = `
<div style="font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; max-width: 600px; margin: 0 auto; background-color: #ffffff; border: 1px solid #e2e8f0;">
    <div style="background-color: #0f172a; padding: 24px; text-align: center;">
        <img src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" alt="PLACID" style="height: 32px; display: block; margin: 0 auto;">
    </div>
    <div style="padding: 32px 24px; color: #334155; line-height: 1.6;">
        <p style="font-size: 16px; margin-bottom: 16px;"><strong>Bonjour ${owner.firstName},</strong></p>
        <p>Suite à notre échange concernant votre bien situé à <strong>${prop?.address}, ${prop?.city}</strong>, j'ai le plaisir de vous transmettre notre étude de rentabilité.</p>
        
        <div style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; margin: 24px 0; border-radius: 4px;">
            <p style="margin: 0; color: #92400e; font-weight: bold; font-size: 12px; text-transform: uppercase;">Revenu Net Annuel Estimé</p>
            <p style="margin: 4px 0 0 0; font-size: 24px; font-weight: bold; color: #b45309;">${financialSnapshot} €</p>
        </div>

        <p>Vous trouverez ci-joint le détail complet de notre proposition de gestion ainsi que nos conditions.</p>
        <p>Cette estimation est basée sur l'analyse de biens similaires dans votre secteur et sur notre expertise du marché local.</p>
        <p>Je reste à votre entière disposition pour en discuter.</p>
        
        <p style="margin-top: 32px;">Cordialement,</p>
        <p style="font-weight: bold; margin: 0; color: #0f172a;">${userSignature}</p>
        <p style="font-weight: bold; margin: 0; color: #0f172a;">Agence Placid ${city}</p>
        <p style="margin: 4px 0 0 0;">${agencyPhone}</p>
    </div>
    <div style="background-color: #f8fafc; padding: 16px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; text-transform: uppercase; letter-spacing: 0.5px;">
        PLACID ${city} • ${agencyLegal} • ${agencyAddress} • SIRET ${agencySiret}
    </div>
</div>
      `;

      setEmailData({
          from: fromString,
          to: owner.email,
          cc: '',
          replyTo: agencyEmail,
          subject: subject,
          body: body
      });
      setIsEmailModalOpen(true);
  };

  const handleConfirmSend = async () => {
      setIsSending(true);
      try {
          // 0. Valider le deal et l'agence avant l'envoi
          const deal = deals.find(d => d.id === selectedDealId);
          if (!deal) {
              toast.error('Deal introuvable');
              setIsSending(false);
              return;
          }
          
          const agency = agencies.find(a => a.id === deal.agencyId);
          if (!agency) {
              toast.error('Aucune agence associée à ce deal. Veuillez sélectionner une agence pour ce deal.');
              setIsSending(false);
              return;
          }
          
          if (!emailDomain) {
              toast.error('Le domaine email n\'est pas configuré. Veuillez le configurer dans les paramètres.');
              setIsSending(false);
              return;
          }
          
          if (!agency.email || !agency.email.endsWith(emailDomain)) {
              toast.error(`L'email de l'agence (${agency.email || 'non défini'}) doit se terminer par ${emailDomain}. Veuillez corriger l'email de l'agence dans les paramètres avant d'envoyer.`);
              setIsSending(false);
              return;
          }
          
          // 1. Generate PDF content
          const pdfContent = await getPdfBase64();
          const reference = (formData as Proposal).reference || 'Placid';
          
          const attachments = pdfContent ? [{
              filename: `Proposition_conciergerie-${reference}.pdf`,
              content: pdfContent,
              type: 'application/pdf'
          }] : undefined;

          // 2. Get Deal
          let dealToUpdate = deals.find(d => d.id === selectedDealId);
          if (!dealToUpdate) return;

          // 3. Ensure Deal Value is Up-to-date (in case user changed numbers but didn't click save)
          const financials = calculateFinancials();
          
          // Create note for update
          const updateNote: Note = {
              id: Date.now().toString(),
              content: `💰 Revenu mensuel estimé mis à jour : ${financials.commissionAmount.toLocaleString()} € (via proposition ${(formData as Proposal).reference})`,
              createdAt: new Date().toISOString(),
              authorName: 'Système'
          };

          const dealWithValue = { 
              ...dealToUpdate, 
              value: financials.commissionAmount,
              notes: [...(dealToUpdate.notes || []), updateNote]
          };
          
          await storage.saveDeal(dealWithValue);
          dealToUpdate = dealWithValue; // Update reference for next step

          // 4. Send Email with Attachment
          await emailService.sendProposal(
              emailData.from,
              emailData.to,
              emailData.cc || undefined,
              emailData.subject,
              emailData.body,
              emailData.replyTo,
              attachments
          );
          
          // 5. Mark Sent (passing the deal that HAS the new value)
          const { deal: finalDeal } = await storage.markProposalSent(formData as Proposal, deal, true);
          
          // 6. Update State
          setDeals(prev => prev.map(d => d.id === finalDeal.id ? finalDeal : d));
          setFormData(prev => ({ ...prev, status: 'SENT' }));
          
          // Refresh List
          storage.getProposals().then(allProposals => {
              if (selectedAgencyId) {
                  const visibleDealIds = new Set(deals.map(d => d.id));
                  setProposals(allProposals.filter(p => visibleDealIds.has(p.dealId)));
              } else {
                  setProposals(allProposals);
              }
          });

          setIsEmailModalOpen(false);
          toast.success("Email avec proposition PDF envoyé avec succès !");
      } catch (e: any) {
          console.error(e);
          toast.error(`Erreur lors de l'envoi : ${e.message}`);
      } finally {
          setIsSending(false);
      }
  };

  // --- FILTERED DEALS FOR DROPDOWN ---
  const filteredDeals = deals
    .filter(d => d.stage !== DealStage.SIGNED && d.stage !== DealStage.LOST) // Active deals only
    .filter(d => !d.proposalId) // RULE: A deal can have only one proposal
    .filter(d => {
        const owner = owners.find(o => o.id === d.ownerId);
        const prop = properties.find(p => p.id === d.propertyId);
        const search = dealSearchQuery.toLowerCase();
        
        return (
            (owner?.firstName || '').toLowerCase().includes(search) ||
            (owner?.lastName || '').toLowerCase().includes(search) ||
            (prop?.address || '').toLowerCase().includes(search) ||
            (prop?.city || '').toLowerCase().includes(search)
        );
    })
    .sort((a,b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()) // Newest first
    .slice(0, 10); // Limit results

  // Handle Deal Selection
  const handleSelectDeal = (d: Deal) => {
      setSelectedDealId(d.id!);
      const p = properties.find(x => x.id === d.propertyId);
      if(p) autoSetSetupFee(p.bedrooms || 0);
      setIsDealDropdownOpen(false);
      setDealSearchQuery('');
  };

  const handleResetSelection = () => {
      setSelectedDealId('');
      setFormData({
          nightlyRate: 90, occupancyRate: 0.70, commissionRate: 25, setupFee: 349
      });
  };

  // --- RENDER ---

  const financials = calculateFinancials();
  const selectedDeal = deals.find(d => d.id === selectedDealId);
  const selectedProperty = properties.find(p => p.id === selectedDeal?.propertyId);
  const selectedOwner = owners.find(o => o.id === selectedDeal?.ownerId);
  const selectedAgency = agencies.find(a => a.id === selectedDeal?.agencyId);

  // Handler for property info modal
  const handlePropertyInfoSave = async (data: Partial<Property>) => {
      if (!propertyForModal) return;
      
      // S'assurer que l'adresse, le code postal et la ville sont bien séparés
      // Si data.address contient déjà le code postal et la ville, ne pas les dupliquer
      const addressParts = {
          address: data.address || propertyForModal.address || '',
          zipCode: data.zipCode || propertyForModal.zipCode || '',
          city: data.city || propertyForModal.city || '',
      };
      
      const updatedProperty = { 
          ...propertyForModal, 
          ...data,
          ...addressParts // Surcharger avec les parties séparées
      } as Property;
      
      const { error } = await storage.saveProperty(updatedProperty);
      if (error) {
          toast.error("Erreur lors de l'enregistrement des informations");
          return;
      }
      
      // Rafraîchir les propriétés
      const updatedProps = await storage.getProperties(selectedAgencyId || null);
      setProperties(updatedProps);
      
      // Mettre à jour les frais de mise en service si le nombre de chambres a changé
      if (updatedProperty.bedrooms !== undefined && updatedProperty.bedrooms !== null) {
          autoSetSetupFee(updatedProperty.bedrooms);
      }
      
      // Si on était en mode "champs manquants", utiliser les données pour l'estimation
      if (missingFieldsList.length > 0) {
          setIsAiLoading(true);
          try {
              // Construire l'adresse complète avec code postal et ville
              const fullAddress = [
                  updatedProperty.address,
                  updatedProperty.zipCode,
                  updatedProperty.city
              ].filter(Boolean).join(', ');
              
              const estimateData = await aiService.estimateProposal({
                  address: fullAddress || updatedProperty.address || '',
                  propertyType: updatedProperty.type,
                  city: updatedProperty.city,
                  surface: updatedProperty.surface || 0,
                  bedrooms: updatedProperty.bedrooms,
                  bathrooms: updatedProperty.bathrooms,
                  maxGuests: updatedProperty.maxGuests,
              });
              
              if (estimateData.nightlyRate && estimateData.occupancyRate) {
                  setFormData(prev => ({
                      ...prev,
                      nightlyRate: estimateData.nightlyRate,
                      occupancyRate: estimateData.occupancyRate
                  }));
                  toast.success("Estimation IA générée !");
              }
          } catch (e: any) {
              console.error("AI Error", e);
              toast.error("Erreur estimation IA: " + (e.message || "Erreur inconnue"));
          } finally {
              setIsAiLoading(false);
          }
      } else {
          // Mode édition simple, juste confirmer la sauvegarde
          toast.success("Informations du bien mises à jour !");
      }
      
      setShowPropertyInfoModal(false);
      setPropertyForModal(null);
      setMissingFieldsList([]);
  };

  // Property Info Modal - shared across all views
  const propertyInfoModal = propertyForModal ? (
      <PropertyInfoModal
          isOpen={showPropertyInfoModal}
          onClose={() => {
              setShowPropertyInfoModal(false);
              setPropertyForModal(null);
              setMissingFieldsList([]);
          }}
          onSave={handlePropertyInfoSave}
          property={propertyForModal}
          missingFields={missingFieldsList}
          showAllFields={missingFieldsList.length === 0} // Afficher tous les champs si aucun champ manquant
      />
  ) : null;

  if (viewMode === 'list') {
      return (
          <>
          <div className="p-8 h-full overflow-y-auto">
              <div className="flex justify-between items-center mb-8">
                  <div>
                      <h2 className="text-2xl font-bold text-slate-900">Propositions Commerciales</h2>
                      <p className="text-slate-500">Gérez vos estimations et mandats envoyés.</p>
                  </div>
                  <button 
                      onClick={() => {
                          toast.info('Veuillez d\'abord sélectionner un deal depuis le Pipeline pour créer une proposition.');
                      }} 
                      className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center font-bold shadow-sm"
                      title="Sélectionnez un deal depuis le Pipeline pour créer une proposition"
                  >
                      <Plus className="w-4 h-4 mr-2" /> Nouvelle Proposition
                  </button>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  <table className="w-full text-left">
                      <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold">
                          <tr>
                              <th className="p-4">Référence</th>
                              <th className="p-4">Agence</th>
                              <th className="p-4">Client</th>
                              <th className="p-4">Bien</th>
                              <th className="p-4 text-right">Est. Annuelle Net</th>
                              <th className="p-4 text-center">Statut</th>
                              <th className="p-4 text-right">Actions</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                          {proposals.map(prop => {
                              const deal = deals.find(d => d.id === prop.dealId);
                              const owner = owners.find(o => o.id === deal?.ownerId);
                              const property = properties.find(p => p.id === deal?.propertyId);
                              const agency = agencies.find(a => a.id === deal?.agencyId);
                              
                              const statusLabel = {
                                  'DRAFT': { text: 'Brouillon', class: 'bg-slate-100 text-slate-500' },
                                  'SENT': { text: 'Envoyée', class: 'bg-blue-100 text-blue-700' },
                                  'ACCEPTED': { text: 'Acceptée', class: 'bg-green-100 text-green-700' },
                                  'REJECTED': { text: 'Rejetée', class: 'bg-red-100 text-red-700' },
                              }[prop.status] || { text: prop.status, class: 'bg-slate-100 text-slate-500' };

                              return (
                                  <tr key={prop.id} className="hover:bg-slate-50 transition-colors">
                                      <td className="p-4">
                                          <div className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2 py-1 rounded w-fit">
                                              {prop.reference || '---'}
                                          </div>
                                          <div className="text-xs text-slate-400 mt-1">{new Date(prop.createdAt).toLocaleDateString()}</div>
                                      </td>
                                      <td className="p-4">
                                          {agency ? (
                                              <span className="text-xs font-bold uppercase text-slate-500 flex items-center">
                                                  <Home className="w-3 h-3 mr-1" /> {agency.city}
                                              </span>
                                          ) : <span className="text-xs text-slate-400">Globale</span>}
                                      </td>
                                      <td className="p-4">
                                          <div className="flex items-center">
                                              <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold mr-3">
                                                  {owner?.firstName[0]}{owner?.lastName[0]}
                                              </div>
                                              <div>
                                                  <div className="font-medium text-slate-900">{owner?.firstName} {owner?.lastName}</div>
                                                  <div className="text-xs text-slate-500">{owner?.email}</div>
                                              </div>
                                          </div>
                                      </td>
                                      <td className="p-4">
                                          <div className="text-sm text-slate-800 font-medium">{property?.type}</div>
                                          <div className="text-xs text-slate-500 flex items-center"><MapPin className="w-3 h-3 mr-1"/> {property?.city}</div>
                                      </td>
                                      <td className="p-4 text-right font-bold text-emerald-600">{prop.estimatedNetOwner?.toLocaleString()} €</td>
                                      <td className="p-4 text-center">
                                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${statusLabel.class}`}>
                                              {statusLabel.text}
                                          </span>
                                      </td>
                                      <td className="p-4 text-right">
                                          <div className="flex items-center justify-end space-x-2">
                                              {prop.status === 'ACCEPTED' && onNavigateToContract && deal && (
                                                  <button 
                                                      onClick={() => onNavigateToContract(prop.dealId)} 
                                                      className="text-purple-600 hover:text-purple-800 p-1.5 hover:bg-purple-50 rounded"
                                                      title="Voir le contrat"
                                                  >
                                                      <PenTool className="w-4 h-4" />
                                                  </button>
                                              )}
                                              <button 
                                                  onClick={() => onNavigateToDeal && onNavigateToDeal(prop.dealId)} 
                                                  className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-100 rounded"
                                                  title="Voir le dossier"
                                              >
                                                  <Briefcase className="w-4 h-4" />
                                              </button>
                                              <button onClick={() => { setFormData(prop); setSelectedDealId(prop.dealId); setViewMode('preview'); }} className="text-amber-600 hover:text-amber-800 font-medium text-sm hover:underline">
                                                  Ouvrir
                                              </button>
                                          </div>
                                      </td>
                                  </tr>
                              );
                          })}
                          {proposals.length === 0 && (
                              <tr><td colSpan={7} className="p-8 text-center text-slate-400 italic">Aucune proposition créée.</td></tr>
                          )}
                      </tbody>
                  </table>
              </div>
          </div>
          {propertyInfoModal}
      </>
      );
  }

  // 2. CREATE / EDIT VIEW (Unchanged logic, just keeping structure)
  if (viewMode === 'create') {
      return (
          <>
          <div className="p-8 h-full overflow-y-auto bg-slate-50">
              <button onClick={() => setViewMode('list')} className="mb-6 text-slate-500 hover:text-slate-800 flex items-center font-medium"><ArrowLeft className="w-4 h-4 mr-2" /> Retour liste</button>
              <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
                  
                  {/* LEFT: FORM */}
                  <div className="lg:col-span-8 space-y-6">
                      
                      {/* STEP 1: SELECT DEAL */}
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200" style={{ zIndex: 30 }}>
                          <h3 className="font-bold text-lg text-slate-800 mb-6 flex items-center">
                              <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3 text-sm">1</span>
                              Le Dossier Client
                          </h3>
                          
                          {/* If a deal is selected, show card */}
                          {selectedDealId && selectedDeal ? (
                              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between animate-in fade-in slide-in-from-top-2">
                                  <div className="flex items-center">
                                      <div className="w-12 h-12 bg-amber-200 text-amber-700 rounded-full flex items-center justify-center font-bold mr-4 shadow-sm border-2 border-white">
                                          {selectedOwner?.firstName.charAt(0)}{selectedOwner?.lastName.charAt(0)}
                                      </div>
                                      <div>
                                          <p className="font-bold text-slate-900 text-lg">{selectedOwner?.firstName} {selectedOwner?.lastName}</p>
                                          <div className="flex items-center text-sm text-slate-600 mt-0.5">
                                              <MapPin className="w-3.5 h-3.5 mr-1 text-slate-400" />
                                              {selectedProperty?.address}, {selectedProperty?.city}
                                          </div>
                                          <span className="inline-block mt-2 text-[10px] uppercase font-bold bg-white px-2 py-0.5 rounded border border-amber-200 text-amber-800">
                                              {selectedDeal.stage}
                                          </span>
                                      </div>
                                  </div>
                                  <button 
                                      onClick={handleResetSelection}
                                      disabled={!!dealId} // Disable change if editing existing prop passed via props
                                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-white rounded-full transition-colors"
                                      title="Changer de dossier"
                                  >
                                      <X className="w-5 h-5" />
                                  </button>
                              </div>
                          ) : (
                              /* Otherwise, show Search Input */
                              <div className="relative" ref={searchContainerRef}>
                                  <div className="relative">
                                      <Search className="absolute left-4 top-3.5 w-5 h-5 text-slate-400" />
                                      <input 
                                          type="text" 
                                          className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none shadow-sm transition-all text-slate-900 bg-slate-50 hover:bg-white focus:bg-white"
                                          placeholder="Rechercher un dossier (Nom client, ville, adresse...)"
                                          value={dealSearchQuery}
                                          onChange={(e) => { setDealSearchQuery(e.target.value); setIsDealDropdownOpen(true); }}
                                          onFocus={() => setIsDealDropdownOpen(true)}
                                      />
                                      {/* Chevron indicator */}
                                      <div className="absolute right-4 top-3.5 pointer-events-none text-slate-400">
                                          <ChevronDown className={`w-5 h-5 transition-transform ${isDealDropdownOpen ? 'rotate-180' : ''}`} />
                                      </div>
                                  </div>

                                  {/* DROPDOWN RESULTS */}
                                  {isDealDropdownOpen && (
                                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                                          {filteredDeals.length > 0 ? (
                                              <>
                                                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wider sticky top-0">
                                                    {dealSearchQuery ? 'Résultats de recherche' : 'Dossiers Récents'}
                                                </div>
                                                <div className="divide-y divide-slate-100">
                                                    {filteredDeals.map(deal => {
                                                        const o = owners.find(x => x.id === deal.ownerId);
                                                        const p = properties.find(x => x.id === deal.propertyId);
                                                        return (
                                                            <div 
                                                                key={deal.id} 
                                                                onClick={() => handleSelectDeal(deal)}
                                                                className="p-3 hover:bg-amber-50 cursor-pointer flex items-center justify-between group transition-colors"
                                                            >
                                                                <div className="flex items-center overflow-hidden">
                                                                    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mr-3 shrink-0 group-hover:bg-white group-hover:text-amber-500 transition-colors">
                                                                        <Briefcase className="w-4 h-4" />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className="text-sm font-bold text-slate-800 truncate group-hover:text-amber-700">
                                                                            {o?.firstName} {o?.lastName}
                                                                        </p>
                                                                        <p className="text-xs text-slate-500 truncate flex items-center">
                                                                            <MapPin className="w-3 h-3 mr-1 inline" /> {p?.city} - {p?.type}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-[10px] px-2 py-1 rounded bg-slate-100 text-slate-500 font-medium group-hover:bg-amber-100 group-hover:text-amber-700 whitespace-nowrap ml-2">
                                                                    {deal.stage}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                              </>
                                          ) : (
                                              <div className="p-8 text-center text-slate-500">
                                                  <p>Aucun dossier actif trouvé sans proposition.</p>
                                                  <p className="text-xs mt-1 text-slate-400">Créez d'abord une affaire dans le Pipeline.</p>
                                              </div>
                                          )}
                                      </div>
                                  )}
                              </div>
                          )}
                      </div>

                      {/* STEP 2: PROPERTY INFO */}
                      {selectedDealId && selectedProperty && (
                          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 animate-in fade-in slide-in-from-bottom-4" style={{ zIndex: 29 }}>
                              <div className="flex justify-between items-center mb-6">
                                  <h3 className="font-bold text-lg text-slate-800 flex items-center">
                                      <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3 text-sm">2</span>
                                      Informations du Bien
                                  </h3>
                                  <button
                                      onClick={() => {
                                          setPropertyForModal(selectedProperty);
                                          setMissingFieldsList([]); // Réinitialiser pour afficher tous les champs
                                          setShowPropertyInfoModal(true);
                                      }}
                                      className="text-xs bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-1.5 rounded-full font-bold flex items-center border border-amber-200 transition-colors"
                                  >
                                      <Edit className="w-3 h-3 mr-1" />
                                      Modifier
                                  </button>
                              </div>
                              
                              {/* Ligne 1 : Adresse | Type | Surface */}
                              <div className="grid grid-cols-3 gap-6 mb-4">
                                  <div className="p-4 bg-gradient-to-r from-slate-50 to-amber-50 rounded-lg border border-slate-100">
                                      <div className="flex items-start gap-2">
                                          <MapPin className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                          <span className="text-sm text-slate-900 leading-relaxed">
                                              {selectedProperty.address || <span className="text-slate-400 italic">Non renseigné</span>}
                                              {selectedProperty.zipCode && `, ${selectedProperty.zipCode}`}
                                              {selectedProperty.city && ` ${selectedProperty.city}`}
                                          </span>
                                      </div>
                                  </div>
                                  
                                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-amber-200 transition-colors">
                                      <div className="flex items-center gap-2">
                                          <Building className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                          <span className="text-base font-semibold text-slate-900">{selectedProperty.type || <span className="text-slate-400 italic font-normal text-sm">Non renseigné</span>}</span>
                                      </div>
                                  </div>
                                  
                                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-amber-200 transition-colors">
                                      <div className="flex items-center gap-2">
                                          <Square className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                          <span className="text-xl font-bold text-slate-900">
                                              {selectedProperty.surface ? (
                                                  <><span>{selectedProperty.surface}</span> <span className="text-sm font-normal text-slate-600 ml-1">m²</span></>
                                              ) : (
                                                  <span className="text-sm text-slate-400 italic font-normal">Non renseigné</span>
                                              )}
                                          </span>
                                      </div>
                                  </div>
                              </div>

                              {/* Ligne 2 : Chambres | SDB | Voyageurs */}
                              <div className="grid grid-cols-3 gap-6">
                                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-amber-200 transition-colors">
                                      <div className="flex items-center gap-2">
                                          <BedDouble className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                          <span className="text-xl font-bold text-slate-900">
                                              {selectedProperty.bedrooms !== undefined && selectedProperty.bedrooms !== null ? selectedProperty.bedrooms : <span className="text-sm text-slate-400 italic font-normal">Non renseigné</span>}
                                          </span>
                                      </div>
                                  </div>
                                  
                                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 hover:border-amber-200 transition-colors">
                                      <div className="flex items-center gap-2">
                                          <Bath className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                          <span className="text-xl font-bold text-slate-900">
                                              {selectedProperty.bathrooms !== undefined && selectedProperty.bathrooms !== null ? selectedProperty.bathrooms : <span className="text-sm text-slate-400 italic font-normal">Non renseigné</span>}
                                          </span>
                                      </div>
                                  </div>
                                  
                                  <div className="p-4 bg-gradient-to-r from-amber-50 to-slate-50 rounded-lg border border-amber-100">
                                      <div className="flex items-center gap-2">
                                          <Users className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                          <span className="text-xl font-bold text-slate-900">
                                              {selectedProperty.maxGuests !== undefined && selectedProperty.maxGuests !== null ? selectedProperty.maxGuests : <span className="text-sm text-slate-400 italic font-normal">Non renseigné</span>}
                                          </span>
                                      </div>
                                  </div>
                              </div>
                          </div>
                      )}

                      {/* ... rest of the component (Perf, Conditions etc.) remains identical ... */}
                      {selectedDealId && (
                          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 animate-in fade-in slide-in-from-bottom-4">
                              <div className="flex justify-between items-center mb-6">
                                  <h3 className="font-bold text-lg text-slate-800 flex items-center">
                                      <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3 text-sm">3</span>
                                      Performance
                                  </h3>
                                  <div className="flex items-center space-x-2">
                                      <a
                                          href="https://www.airbnb.fr/host/homes"
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-xs bg-rose-50 text-rose-600 hover:bg-rose-100 px-3 py-1.5 rounded-full font-bold flex items-center border border-rose-200 transition-colors no-underline"
                                      >
                                          <ExternalLink className="w-3 h-3 mr-1" />
                                          Estimation AirBnb
                                      </a>
                                      <button 
                                          onClick={handleAiEstimate} 
                                          disabled={isAiLoading}
                                          className="text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 px-3 py-1.5 rounded-full font-bold flex items-center border border-purple-200 transition-colors"
                                      >
                                          {isAiLoading ? <Loader className="w-3 h-3 animate-spin mr-1"/> : <Sparkles className="w-3 h-3 mr-1" />}
                                          Estimation IA <sup className="ml-0.5 text-[9px]">beta</sup>
                                      </button>
                                  </div>
                              </div>
                              <div className="grid grid-cols-2 gap-6">
                                  <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-2">Prix Moyen Nuitée (€)</label>
                                      <div className="relative">
                                          <input 
                                            type="number" 
                                            className="w-full pl-4 pr-12 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 font-bold focus:ring-2 focus:ring-amber-500 outline-none" 
                                            value={formData.nightlyRate} 
                                            onChange={e => setFormData({...formData, nightlyRate: Number(e.target.value)})} 
                                          />
                                          <span className="absolute right-4 top-3.5 text-slate-400 font-medium">€</span>
                                      </div>
                                  </div>
                                  <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-2">Taux d'Occupation (%)</label>
                                      <div className="relative">
                                          <input 
                                            type="number" 
                                            step="1" 
                                            max="100"
                                            min="0"
                                            className="w-full pl-4 pr-12 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 font-bold focus:ring-2 focus:ring-amber-500 outline-none" 
                                            // Display as integer percentage (e.g. 70 for 0.70)
                                            value={formData.occupancyRate !== undefined ? Math.round(formData.occupancyRate * 100) : ''} 
                                            // Store as decimal (e.g. 0.70 for 70)
                                            onChange={e => setFormData({...formData, occupancyRate: Number(e.target.value) / 100})} 
                                          />
                                          <span className="absolute right-4 top-3.5 text-slate-400 font-medium">%</span>
                                      </div>
                                      <p className="text-xs text-slate-500 mt-1">Ex: 75 pour 75%</p>
                                  </div>
                              </div>
                          </div>
                      )}

                      {selectedDealId && (
                          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 animate-in fade-in slide-in-from-bottom-4">
                              <h3 className="font-bold text-lg text-slate-800 mb-6 flex items-center">
                                  <span className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center mr-3 text-sm">3</span>
                                  Conditions Commerciales
                              </h3>
                              
                              <div className="mb-6">
                                  <label className="block text-sm font-medium text-slate-700 mb-2">Commission Placid (%)</label>
                                  <div className="relative max-w-xs">
                                      <input 
                                        type="number" 
                                        className="w-full pl-4 pr-12 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 font-bold focus:ring-2 focus:ring-amber-500 outline-none" 
                                        value={formData.commissionRate} 
                                        onChange={e => setFormData({...formData, commissionRate: Number(e.target.value)})} 
                                      />
                                      <span className="absolute right-4 top-3.5 text-slate-400 font-medium">%</span>
                                  </div>
                              </div>

                              <div className="pt-6 border-t border-slate-100">
                                  <div className="flex justify-between items-center mb-4">
                                      <label className="text-sm font-bold text-slate-900">Frais de mise en service (Setup)</label>
                                      <div className="flex items-center">
                                          <input 
                                            type="checkbox" 
                                            id="offerSetup"
                                            className="w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500 mr-2"
                                            checked={formData.isSetupFeeOffered} 
                                            onChange={e => setFormData({...formData, isSetupFeeOffered: e.target.checked})} 
                                          />
                                          <label htmlFor="offerSetup" className="text-sm text-slate-700 cursor-pointer font-medium">Offrir les frais</label>
                                      </div>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                      {SETUP_TIERS.map((tier) => (
                                          <div 
                                            key={tier.label}
                                            onClick={() => !formData.isSetupFeeOffered && setFormData({...formData, setupFee: tier.price})}
                                            className={`border rounded-lg p-2 cursor-pointer transition-all text-center flex flex-col justify-center min-h-[70px] ${formData.setupFee === tier.price ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-slate-200 hover:border-slate-300'} ${formData.isSetupFeeOffered ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          >
                                              <p className="text-[10px] text-slate-500 font-medium mb-1">{tier.label}</p>
                                              <p className="font-bold text-slate-900 text-sm">{tier.price} €</p>
                                          </div>
                                      ))}
                                      {/* MANUAL INPUT CARD */}
                                      <div className={`border rounded-lg p-2 flex flex-col justify-center min-h-[70px] ${!SETUP_TIERS.find(t => t.price === formData.setupFee) ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : 'border-slate-200'}`}>
                                          <p className="text-[10px] text-slate-500 font-medium mb-1 text-center">Autre Montant</p>
                                          <div className="relative">
                                              <input 
                                                type="number" 
                                                className="w-full p-1 text-center font-bold text-sm bg-transparent outline-none border-b border-slate-300 focus:border-amber-500 text-slate-900" 
                                                value={formData.setupFee}
                                                onChange={e => setFormData({...formData, setupFee: Number(e.target.value)})}
                                                disabled={formData.isSetupFeeOffered}
                                              />
                                          </div>
                                      </div>
                                  </div>
                                  <p className="text-xs text-slate-400 mt-3 italic flex items-center">
                                      <Info className="w-3 h-3 mr-1" />
                                      Inclut : Photos HD, Création annonces, Optimisation, Ménage initial.
                                  </p>
                              </div>
                          </div>
                      )}
                  </div>

                  {/* RIGHT: PREVIEW STICKY */}
                  <div className="lg:col-span-4">
                      <div className="bg-slate-900 text-white p-6 rounded-xl shadow-xl sticky top-6">
                          <h3 className="text-lg font-bold mb-6 border-b border-slate-700 pb-4">Aperçu Rapide</h3>
                          
                          <div className="space-y-6">
                              <div className="flex justify-between items-end">
                                  <span className="text-slate-400 text-sm">CA Mensuel Est.</span>
                                  <span className="text-2xl font-bold">{financials.monthlyGross.toLocaleString()} €</span>
                              </div>
                              
                              <div className="flex justify-between items-center text-sm text-slate-400">
                                  <span>Commission ({formData.commissionRate}%)</span>
                                  <span className="text-red-400">- {Math.round(financials.monthlyGross * ((formData.commissionRate||25)/100))} €</span>
                              </div>

                              <div className="pt-4 border-t border-slate-700">
                                  <p className="text-amber-400 text-xs uppercase font-bold tracking-wider mb-1">Revenu Net Propriétaire</p>
                                  <div className="flex items-baseline">
                                      <p className="text-4xl font-bold text-white">{financials.netAnnual.toLocaleString()}</p>
                                      <span className="ml-2 text-amber-400 text-lg">€ / an</span>
                                  </div>
                                  <p className="text-sm text-slate-400 mt-2">soit ~{financials.netMonthly.toLocaleString()} € / mois net</p>
                              </div>
                          </div>

                          <button 
                              onClick={() => handleSave('DRAFT')}
                              disabled={!selectedDealId}
                              className="w-full mt-8 bg-white hover:bg-slate-100 text-slate-900 font-bold py-3.5 rounded-lg flex items-center justify-center transition-all disabled:opacity-50 shadow-lg"
                          >
                              <FileText className="w-4 h-4 mr-2" /> 
                              Générer le PDF
                          </button>
                      </div>
                  </div>
              </div>
          </div>
          {propertyInfoModal}
      </>
      );
  }

  // 3. PREVIEW & SEND VIEW
  if (viewMode === 'preview' && selectedDeal) {
      return (
          <>
          <div className="p-8 h-full flex flex-col relative bg-slate-100 overflow-y-auto">
              {/* MODAL EMAIL ... */}
              {isEmailModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg text-slate-800 flex items-center">
                                <Mail className="w-5 h-5 mr-2 text-amber-500" />
                                Envoyer la proposition
                            </h3>
                            <button onClick={() => setIsEmailModalOpen(false)} className="hover:bg-slate-200 rounded-full p-1"><X className="w-5 h-5 text-slate-500" /></button>
                        </div>
                        
                        <div className="p-6 space-y-4 overflow-y-auto bg-slate-50/50">
                            {/* Metadata Inputs */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">À (Destinataire)</label>
                                    <input 
                                        className="w-full p-2 border border-slate-300 rounded bg-white text-sm" 
                                        value={emailData.to} 
                                        onChange={e => setEmailData({...emailData, to: e.target.value})} 
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">CC (Copie)</label>
                                    <input 
                                        className="w-full p-2 border border-slate-300 rounded bg-white text-sm" 
                                        placeholder="email@exemple.com"
                                        value={emailData.cc} 
                                        onChange={e => setEmailData({...emailData, cc: e.target.value})} 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Objet</label>
                                <input 
                                    className="w-full p-2 border border-slate-300 rounded bg-white text-sm font-medium" 
                                    value={emailData.subject} 
                                    onChange={e => setEmailData({...emailData, subject: e.target.value})} 
                                />
                            </div>

                            {/* Rich Text Editor */}
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1 uppercase">Message</label>
                                <RichTextEditor 
                                    value={emailData.body} 
                                    onChange={(val) => setEmailData({...emailData, body: val})} 
                                />
                            </div>
                        </div>

                        <div className="p-4 bg-white border-t border-slate-100 flex justify-between items-center">
                            <div className="text-xs text-slate-400">
                                Expéditeur: <span className="font-medium text-slate-600">{emailData.from}</span>
                            </div>
                            <div className="flex space-x-3">
                                <button onClick={() => setIsEmailModalOpen(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-colors">Annuler</button>
                                <button onClick={handleConfirmSend} disabled={isSending} className="px-6 py-2 bg-slate-900 text-white rounded-lg font-bold flex items-center hover:bg-slate-800 disabled:opacity-70 transition-colors shadow-lg shadow-slate-900/20">
                                    {isSending ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                    Envoyer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-6 no-print max-w-[210mm] mx-auto w-full">
                  <div className="flex items-center gap-2">
                      <button onClick={() => setViewMode('list')} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50 transition-colors"><ArrowLeft className="w-4 h-4 mr-2" /> Retour</button>
                      <button onClick={() => onNavigateToDeal && onNavigateToDeal(selectedDealId)} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50 transition-colors">
                          <Briefcase className="w-4 h-4 mr-2" /> Lead
                      </button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                      {formData.status === 'ACCEPTED' ? (
                          <>
                            {/* Workflow Logic: ACCEPTED Proposal leads to Contract generation, not directly to Managed Property */}
                            {onNavigateToContract && selectedDeal && (
                                <button 
                                    onClick={() => onNavigateToContract(selectedDeal.id!)} 
                                    className="bg-slate-900 text-white px-4 h-10 rounded-lg font-medium flex items-center hover:bg-slate-800 shadow-sm transition-colors"
                                >
                                    <PenTool className="w-4 h-4 mr-2" /> 
                                    {selectedDeal.contractId ? 'Voir le contrat' : 'Générer le contrat'}
                                </button>
                            )}
                            <div className="bg-green-600 text-white px-4 h-10 rounded-lg font-bold flex items-center shadow-sm">
                                <Check className="w-4 h-4 mr-2" /> ACCEPTÉE
                            </div>
                          </>
                      ) : (
                          <>
                            <button onClick={() => setViewMode('create')} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50 transition-colors">
                                <Edit className="w-4 h-4 mr-2" /> Modifier
                            </button>
                            <button 
                                onClick={generatePDF} 
                                disabled={isDownloadingPdf}
                                className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center shadow-sm hover:bg-slate-50 transition-colors"
                            >
                                {isDownloadingPdf ? <Loader className="w-4 h-4 animate-spin mr-2"/> : <Download className="w-4 h-4 mr-2" />}
                                PDF
                            </button>
                            
                            {/* MARK ACCEPTED BUTTON - UPDATED LOGIC */}
                            <button 
                                onClick={handleAcceptProposal} 
                                className="bg-white border border-slate-200 text-emerald-600 px-4 h-10 rounded-lg font-medium flex items-center hover:bg-emerald-50 shadow-sm border-emerald-200 transition-colors"
                            >
                                <Check className="w-4 h-4 mr-2" /> Valider
                            </button>

                            {formData.status !== 'SENT' && (
                                <>
                                    <button onClick={() => handleSave('SENT')} className="bg-white border border-slate-200 text-slate-700 px-4 h-10 rounded-lg font-medium flex items-center hover:bg-slate-50 shadow-sm transition-colors"><CheckCircle2 className="w-4 h-4 mr-2" /> Envoyée</button>
                                    <button onClick={handlePrepareEmail} disabled={isSending} className="bg-slate-900 text-white px-4 h-10 rounded-lg font-medium flex items-center hover:bg-slate-800 shadow-sm transition-colors">
                                        <Mail className="w-4 h-4 mr-2" /> Email
                                    </button>
                                </>
                            )}
                          </>
                      )}
                  </div>
              </div>

              {/* PDF CONTENT AREA */}
              <div id="proposal-pdf-content" className="bg-white shadow-2xl mx-auto print-area flex flex-col font-sans text-slate-900" style={{ width: '794px', height: '1120px', padding: 0 }}>
                  {/* ... PDF Content implementation remains same ... */}
                  {/* HERO HEADER - COMPACTED */}
                  <div className="bg-slate-900 text-white p-8 relative overflow-hidden">
                      <div className="relative z-10 flex justify-between items-start flex-row">
                          <div>
                              <img src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" alt="PLACID" className="h-10 object-contain mb-4 p-1 rounded" />
                              <h1 className="text-3xl font-bold leading-tight mb-2">Proposition de gestion<br/>en conciergerie</h1>
                              <div className="mt-4 flex items-center text-slate-300 text-sm">
                                  <span className="mr-8">Date : {new Date().toLocaleDateString()}</span>
                                  <span>Réf : {(formData as Proposal).reference}</span>
                              </div>
                          </div>
                          <div className="text-right">
                              <p className="text-slate-400 text-xs uppercase tracking-widest mb-1">Préparé pour</p>
                              <h2 className="text-xl font-bold text-white">{selectedOwner?.firstName} {selectedOwner?.lastName}</h2>
                              <p className="text-slate-300 text-sm mt-1">{selectedOwner?.email}</p>
                          </div>
                      </div>
                  </div>

                  <div className="p-8 flex-1 flex flex-col justify-between">
                      
                      {/* PROPERTY SUMMARY */}
                      <div className="mb-6">
                          <h3 className="text-slate-900 font-bold uppercase tracking-wider text-xs mb-3 border-l-4 border-amber-500 pl-3">Votre Bien</h3>
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center justify-between flex-row">
                              <div>
                                  <p className="text-lg font-bold text-slate-900">{selectedProperty?.address}</p>
                                  <p className="text-slate-500 text-base">{selectedProperty?.zipCode} {selectedProperty?.city}</p>
                              </div>
                              <div className="flex space-x-12 text-base">
                                  <div className="text-center">
                                      <p className="text-slate-400 uppercase text-[10px] font-bold">Type</p>
                                      <p className="font-semibold text-slate-800">{selectedProperty?.type}</p>
                                  </div>
                                  <div className="text-center">
                                      <p className="text-slate-400 uppercase text-[10px] font-bold">Surface</p>
                                      <p className="font-semibold text-slate-800">{selectedProperty?.surface} m²</p>
                                  </div>
                                  <div className="text-center">
                                      <p className="text-slate-400 uppercase text-[10px] font-bold">Chambres</p>
                                      <p className="font-semibold text-slate-800">{selectedProperty?.bedrooms || 0}</p>
                                  </div>
                              </div>
                          </div>
                      </div>

                      {/* DETAILED CALCULATION */}
                      <div className="mb-6">
                          <h3 className="text-slate-900 font-bold uppercase tracking-wider text-xs mb-3 border-l-4 border-amber-500 pl-3">Étude de Rentabilité Mensuelle</h3>
                          
                          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                              {/* Row 1: Gross */}
                              <div className="flex justify-between items-center p-4 border-b border-slate-100 bg-slate-50 flex-row">
                                  <div>
                                      <p className="font-bold text-slate-800 text-lg">Revenu Brut Estimé</p>
                                      <p className="text-sm text-slate-500">Basé sur {formData.nightlyRate}€/nuit et {((formData.occupancyRate||0)*100).toFixed(0)}% d'occupation</p>
                                  </div>
                                  <p className="font-bold text-slate-900 text-xl">{financials.monthlyGross.toLocaleString()} €</p>
                              </div>

                              {/* Row 2: Commission */}
                              <div className="flex justify-between items-center p-4 border-b border-slate-100 flex-row">
                                  <div>
                                      <p className="font-medium text-slate-700 text-base">Commission Placid</p>
                                      <p className="text-sm text-slate-500">Gestion complète ({formData.commissionRate}%)</p>
                                  </div>
                                  <p className="font-medium text-red-600 text-base">- {Math.round(financials.monthlyGross * ((formData.commissionRate||25)/100)).toLocaleString()} €</p>
                              </div>

                              {/* Row 3: Cleaning */}
                              <div className="flex justify-between items-center p-4 border-b border-slate-100 flex-row">
                                  <div>
                                      <p className="font-medium text-slate-700 text-base">Frais de Ménage & Blanchisserie</p>
                                      <p className="text-sm text-slate-500">Intégralement payés par les voyageurs</p>
                                  </div>
                                  <p className="font-medium text-slate-900 text-base">0 €</p>
                              </div>

                              {/* Row 4: Net */}
                              <div className="flex justify-between items-center p-5 bg-slate-900 text-white flex-row">
                                  <div>
                                      <p className="font-bold uppercase tracking-wider text-amber-500 text-sm">Revenus Nets Estimés</p>
                                      <p className="text-sm text-slate-400">Montant reversé sur votre compte</p>
                                  </div>
                                  <div className="text-right">
                                      <p className="font-bold text-3xl">{financials.netMonthly.toLocaleString()} € <span className="text-sm font-normal text-slate-400">/ mois</span></p>
                                      <p className="text-sm text-slate-400 mt-1">soit env. {financials.netAnnual.toLocaleString()} € / an</p>
                                  </div>
                              </div>
                          </div>
                      </div>

                      {/* SETUP FEES */}
                      <div className="mb-6">
                          <h3 className="text-slate-900 font-bold uppercase tracking-wider text-xs mb-3 border-l-4 border-amber-500 pl-3">Frais de Mise en Service</h3>
                          
                          <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-100 flex-row">
                              <div>
                                  <p className="font-bold text-slate-900 mb-2 text-base">Pack de Démarrage</p>
                                  <ul className="text-sm text-slate-600 space-y-1.5">
                                      <li className="flex items-center"><Check className="w-4 h-4 text-amber-500 mr-2" /> Shooting photo professionnel HDR</li>
                                      <li className="flex items-center"><Check className="w-4 h-4 text-amber-500 mr-2" /> Rédaction et optimisation des annonces</li>
                                      <li className="flex items-center"><Check className="w-4 h-4 text-amber-500 mr-2" /> Paramétrage des outils de tarification (Yield)</li>
                                      <li className="flex items-center"><Check className="w-4 h-4 text-amber-500 mr-2" /> Création du livret d'accueil digital</li>
                                  </ul>
                              </div>
                              <div className="text-right">
                                  {formData.isSetupFeeOffered ? (
                                      <div className="flex flex-col items-end">
                                        <div className="relative mb-1 inline-block">
                                            <span className="text-sm text-slate-400 font-medium relative z-10">{formData.setupFee} €</span>
                                            {/* Custom strike-through for PDF compatibility */}
                                            <div className="absolute top-1/2 left-0 w-full h-[1px] bg-slate-400 -translate-y-1/2 z-20"></div>
                                        </div>
                                        <p className="text-2xl font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded border border-emerald-200 inline-block">OFFERTS</p>
                                      </div>
                                  ) : (
                                      <p className="text-3xl font-bold text-slate-900">{formData.setupFee} € <span className="text-sm font-normal text-slate-500">HT</span></p>
                                  )}
                              </div>
                          </div>
                      </div>

                      {/* FOOTER */}
                      <div className="mt-auto pt-8 border-t border-slate-200 text-center">
                          <p className="text-[10px] text-slate-400 italic mb-3">
                              * Cette proposition est une estimation basée sur des données de marché et ne constitue pas un engagement contractuel de revenu. Les performances réelles peuvent varier.
                          </p>
                          <div className="flex justify-center items-center text-[10px] text-slate-500 font-medium uppercase tracking-widest mt-2 flex-wrap gap-x-4">
                              {selectedAgency ? (
                                  <>
                                    <span>PLACID {selectedAgency.city}</span>
                                    <span>•</span>
                                    <span>{selectedAgency.legalStructure}</span>
                                    <span>•</span>
                                    <span>{selectedAgency.address}</span>
                                    <span>•</span>
                                    <span>SIRET {selectedAgency.siret}</span>
                                  </>
                              ) : (
                                  <span className="text-red-400 font-bold bg-red-50 px-2 py-1 rounded">Agence non identifiée</span>
                              )}
                          </div>
                      </div>
                  </div>
              </div>
          </div>
          {propertyInfoModal}
      </>
      );
  }

  return (
      <>
          <div>Chargement...</div>
          {propertyInfoModal}
      </>
  );
};
